// SS-4412: the practice's payment methods on the billing page. A non-partner practice may add a
// US bank account (Stripe ACH Direct Debit, through Financial Connections or micro-deposits):
// it becomes the primary payment method and the card stays on file as the backup. A bank
// account is offered, never required — nothing here opens by itself or blocks the page.
// Partner practices (the API answers bank_account_available: false) see only the card, as before.
import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard, faCircle, faBuildingColumns } from "@fortawesome/free-solid-svg-icons";
import { faCcVisa, faCcMastercard, faCcAmex, faCcDiscover } from "@fortawesome/free-brands-svg-icons";
import { loadStripe } from "@stripe/stripe-js";
import { cqAPI } from "../../lib/Auth";

// Stripe reports card brands in lower case ("visa", "mastercard", "amex", "discover").
export const cardBrandIcon = (brand) => {
  switch (String(brand || "").toLowerCase().replace(/\s+/g, "")) {
    case "visa":
      return faCcVisa;
    case "mastercard":
      return faCcMastercard;
    case "amex":
    case "americanexpress":
      return faCcAmex;
    case "discover":
      return faCcDiscover;
    default:
      return faCreditCard;
  }
};

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PKEY);
  }
  return stripePromise;
};

// Writes go out ONCE: a replayed setup or record could create a second SetupIntent or record a
// bank account the practice has since replaced.
const SINGLE_ATTEMPT = { attempts: 1 };
// ...and are bounded, so a stalled answer never leaves the controls disabled. A record may pay the
// open invoices and replay a parked subscription, hence the generous bound. An answer that never
// came is read back from the API, never sent again.
export const BANK_WRITE_TIMEOUT_MS = 60000;
const SETUP_ENDPOINT = "/setting/subscriptions/bank-account/setup";
const BANK_ENDPOINT = "/setting/subscriptions/bank-account";

// Stripe's ACH Direct Debit mandate text, shown before the debit authorization is confirmed.
export const MANDATE_TEXT =
  'By clicking "Agree and save", you authorize Viva to debit the bank account specified above for ' +
  "any amount owed for charges arising from your use of Viva's services, pursuant to Viva's terms, " +
  "until this authorization is revoked. You may amend or cancel this authorization at any time by " +
  "providing notice to Viva with 30 (thirty) days notice. If you use Viva's services or pay " +
  "invoices on a recurring basis, you authorize Viva to debit the bank account periodically. " +
  "Payments that fall outside of the regular debits authorized above will only be debited after " +
  "your authorization is obtained.";

function MaskedDigits({ last4 }) {
  return (
    <Flex align="center">
      <Flex align="center" justify="center">
        {[...Array(4)].map((_, i) => (
          <FontAwesomeIcon key={i} icon={faCircle} style={{ fontSize: "6px", paddingLeft: "1px" }} />
        ))}
      </Flex>
      <Text marginLeft="4px">{last4}</Text>
    </Flex>
  );
}

export function CreditCardDisplay({ last4, brand }) {
  return (
    <Flex align="center" data-testid="card-display">
      <FontAwesomeIcon icon={cardBrandIcon(brand)} data-testid="card-brand-icon" data-brand={String(brand || "").toLowerCase()} />
      <Flex align="center" justify="center">
        {[...Array(12)].map((_, i) => (
          <FontAwesomeIcon
            key={i}
            icon={faCircle}
            style={{
              fontSize: "6px",
              paddingLeft: i % 4 === 0 && i !== 0 ? "10px" : "1px",
            }}
          />
        ))}
      </Flex>{" "}
      <Text data-testid="card-last4">{last4}</Text>
    </Flex>
  );
}

// The whole bank collection: a SetupIntent from the API, Stripe's Financial Connections dialog,
// the mandate, the confirmation, and the result recorded by the API. Returns the API's answer,
// or null when the user closed Stripe's dialog without choosing an account.
export async function collectBankAccount({ stripe, accountHolderName, confirmMandate }) {
  const setup = await cqAPI.post(SETUP_ENDPOINT, {}, BANK_WRITE_TIMEOUT_MS, SINGLE_ATTEMPT);
  if (setup?.type !== "ok" || !setup.data?.client_secret) {
    throw new Error(setup?.msg || "The bank account setup could not be started.");
  }
  const clientSecret = setup.data.client_secret;
  const collected = await stripe.collectBankAccountForSetup({
    clientSecret,
    params: {
      payment_method_type: "us_bank_account",
      // Stripe sends the mandate confirmation and micro-deposit notices to this email
      payment_method_data: {
        billing_details: { name: accountHolderName, ...(setup.data.email ? { email: setup.data.email } : {}) },
      },
    },
    expand: ["payment_method"],
  });
  if (collected.error) {
    throw new Error(collected.error.message);
  }
  if (collected.setupIntent?.status === "requires_payment_method") {
    return null; // the dialog was closed
  }
  if (collected.setupIntent?.status === "requires_confirmation") {
    // the mandate names the account it authorizes
    const account = collected.setupIntent.payment_method?.us_bank_account || null;
    if (!(await confirmMandate(account))) {
      return null;
    }
    const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret);
    if (confirmed.error) {
      throw new Error(confirmed.error.message);
    }
  }
  const recorded = await cqAPI.post(BANK_ENDPOINT, { setup_intent_id: setup.data.setup_intent_id }, BANK_WRITE_TIMEOUT_MS, SINGLE_ATTEMPT);
  if (recorded?.type !== "ok") {
    throw new Error(recorded?.msg || "The bank account could not be saved.");
  }
  return recorded.data;
}

function PaymentMethods({ card, paymentMethods = [], bankAccountPending = null, bankAccountAvailable = false, businessName = "", onUpdateCard, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState(businessName || "");
  const [mandate, setMandate] = useState(null); // {resolve, account} while the mandate is shown

  const bank = paymentMethods.find((pm) => pm.type === "us_bank_account") || null;
  const listedCard = paymentMethods.find((pm) => pm.type === "card") || null;
  const shownCard = listedCard || (card?.last4 ? { ...card, type: "card", default: !bank?.default } : null);
  const bankIsPrimary = !!bank?.default;

  const fail = (message) => toast({ title: "Error", description: message, status: "error", duration: 9000, isClosable: true });

  const confirmMandate = (account) => new Promise((resolve) => setMandate({ resolve, account }));
  const closeMandate = (agreed) => {
    if (mandate) mandate.resolve(agreed);
    setMandate(null);
  };

  // After every write, failed ones too: what the API now holds is read back. A save whose answer
  // was lost may have gone through; it is shown as it is, never sent again.
  const refresh = async (changed) => {
    try {
      await onChanged?.(changed);
    } catch (error) {
      console.error("Cannot refresh the payment methods:", error);
    }
  };

  const addBankAccount = async () => {
    setNameModalOpen(false);
    setBusy(true);
    let result = null;
    try {
      const stripe = await getStripe();
      if (!stripe) throw new Error("Stripe could not be loaded. Please try again.");
      result = await collectBankAccount({ stripe, accountHolderName: accountHolderName.trim(), confirmMandate });
      if (result) {
        toast({
          title: result.status === "pending_verification" ? "Verification pending" : "Bank account added",
          description: result.status === "pending_verification"
            ? "Stripe sent small deposits to your account. Verify them to make this bank account your primary payment method."
            : "Your bank account is now your primary payment method. Your card stays on file as a backup.",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      fail(error.message);
    } finally {
      await refresh(result);
      setBusy(false);
    }
  };

  const checkVerification = async () => {
    setBusy(true);
    let changed = null;
    try {
      const response = await cqAPI.post(BANK_ENDPOINT, { setup_intent_id: bankAccountPending.setup_intent_id }, BANK_WRITE_TIMEOUT_MS, SINGLE_ATTEMPT);
      if (response?.type !== "ok") throw new Error(response?.msg || "The verification could not be checked.");
      if (response.data?.status === "pending_verification") {
        toast({ title: "Still pending", description: "The bank account is not verified yet.", status: "info", duration: 6000, isClosable: true });
      }
      changed = response.data;
    } catch (error) {
      fail(error.message);
    } finally {
      await refresh(changed);
      setBusy(false);
    }
  };

  const removeBankAccount = async () => {
    setBusy(true);
    let changed = null;
    try {
      const response = await cqAPI.del(BANK_ENDPOINT, {}, BANK_WRITE_TIMEOUT_MS, SINGLE_ATTEMPT);
      if (response?.type !== "ok") throw new Error(response?.msg || "The bank account could not be removed.");
      changed = response.data;
    } catch (error) {
      fail(error.message);
    } finally {
      await refresh(changed);
      setBusy(false);
    }
  };

  // A partner practice, or an API without the bank account: the card button as it always was.
  if (!bankAccountAvailable) {
    return (
      <Button marginLeft="20px" leftIcon={<CreditCardDisplay last4={shownCard?.last4} brand={shownCard?.brand} />} onClick={onUpdateCard} data-testid="update-card">
        {!shownCard?.last4 && <Text>Add Credit Card</Text>}
      </Button>
    );
  }

  return (
    <Box marginLeft="20px" data-testid="payment-methods">
      {bank && (
        <HStack spacing={3} mb={2} data-testid="bank-account-row">
          <FontAwesomeIcon icon={faBuildingColumns} />
          <Text>{bank.bank_name || "Bank account"}</Text>
          <MaskedDigits last4={bank.last4} />
          {bankIsPrimary && <Badge colorScheme="green" data-testid="bank-account-primary">Primary</Badge>}
          <Button size="sm" onClick={() => setNameModalOpen(true)} isDisabled={busy}>Replace</Button>
          <Button size="sm" variant="outline" onClick={removeBankAccount} isDisabled={busy} data-testid="remove-bank-account">Remove</Button>
        </HStack>
      )}
      {bankAccountPending && (
        <HStack spacing={3} mb={2} data-testid="bank-account-pending">
          <Badge colorScheme="yellow">Verification pending</Badge>
          {bankAccountPending.verification_url && (
            <Link href={bankAccountPending.verification_url} isExternal color="blue.500" data-testid="bank-account-verification-link">
              Verify micro-deposits
            </Link>
          )}
          <Button size="sm" onClick={checkVerification} isDisabled={busy}>Check verification</Button>
          {!bank && (
            <Button size="sm" variant="outline" onClick={removeBankAccount} isDisabled={busy}>Cancel</Button>
          )}
        </HStack>
      )}
      <HStack spacing={3}>
        <Button leftIcon={<CreditCardDisplay last4={shownCard?.last4} brand={shownCard?.brand} />} onClick={onUpdateCard} isDisabled={busy} data-testid="update-card">
          {shownCard?.last4 ? <Text>Update card</Text> : <Text>Add Credit Card</Text>}
        </Button>
        {shownCard?.last4 && bankIsPrimary && <Badge data-testid="card-backup">Backup</Badge>}
        {!bank && !bankAccountPending && (
          <Button onClick={() => setNameModalOpen(true)} isDisabled={busy} isLoading={busy} data-testid="add-bank-account">
            Add bank account
          </Button>
        )}
      </HStack>

      <Modal isOpen={nameModalOpen} onClose={() => setNameModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{bank ? "Replace bank account" : "Add bank account"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              Pay Viva directly from your practice's US bank account. It becomes your primary payment method; your card stays on file as a backup.
            </Text>
            <Text fontWeight="semibold" mb={1}>Account holder name</Text>
            <Input value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} data-testid="account-holder-name" />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setNameModalOpen(false)}>Cancel</Button>
            <Button onClick={addBankAccount} isDisabled={!accountHolderName.trim()} data-testid="continue-bank-account">Continue</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!mandate} onClose={() => closeMandate(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Authorize debits</ModalHeader>
          <ModalBody>
            {mandate?.account && (
              <HStack spacing={3} mb={3} data-testid="mandate-account">
                <FontAwesomeIcon icon={faBuildingColumns} />
                <Text>{mandate.account.bank_name || "Bank account"}</Text>
                <MaskedDigits last4={mandate.account.last4} />
              </HStack>
            )}
            <Text fontSize="sm" data-testid="ach-mandate">{MANDATE_TEXT}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => closeMandate(false)}>Cancel</Button>
            <Button onClick={() => closeMandate(true)} data-testid="agree-mandate">Agree and save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PaymentMethods;
